import type { CheckStatus, HoldState, Reservation, ServiceState, WorkspaceState } from '@vinz/shared';
import { useAuth } from '../auth.js';
import { formatAgo, formatLeft, formatLocal, viewerTimeZone } from '../lib/format.js';
import { useStateQuery } from '../use-api.js';

const GITHUB_ORG = 'rewindio';
const R5_TITLE = 'Holds arrive with R5 — there is nothing to act on yet.';
const BOT_CREATOR = 'github-actions[bot]';

function commitUrl(repo: string, sha: string): string {
  return `https://github.com/${GITHUB_ORG}/${repo}/commit/${sha}`;
}

function branchUrl(repo: string, ref: string): string {
  return `https://github.com/${GITHUB_ORG}/${repo}/tree/${ref}`;
}

const ROW_CLASS: Record<HoldState, string> = {
  free: '',
  held: 'row-held',
  restoring: 'row-restoring',
  restore_failed: 'row-restore-failed',
};

/**
 * The one workspace a collapsed row is allowed to speak for. `workspaces[0]` is position 0 of the
 * CONFIGURED list, observed or not, so on a service whose first workspace has never reported it
 * spoke for the whole service by saying nothing. A row only ever collapses when every observed
 * workspace agrees (see `mixed` in the API's state handler), so the first workspace that actually
 * has a reading represents all of them; when none does, there is genuinely nothing to show.
 */
export function representativeWorkspace(workspaces: WorkspaceState[]): WorkspaceState | undefined {
  return workspaces.find((w) => w.sha !== undefined);
}

function AttributionName({ attribution }: { attribution: WorkspaceState['attribution'] }) {
  if (attribution.kind === 'person') return <span>{attribution.name}</span>;
  if (attribution.kind === 'ci') return <span className="attribution-mechanism">CI</span>;
  return <span className="attribution-mechanism">Vinz · auto-restore</span>;
}

function DeployedByCell({ workspace, nowMs }: { workspace?: WorkspaceState; nowMs: number }) {
  if (!workspace) return <span>—</span>;
  return (
    <div>
      <AttributionName attribution={workspace.attribution} />
      {workspace.actorIsBot && <div className="attribution-via">via {BOT_CREATOR}</div>}
      {workspace.deployedAt && (
        <div className="deployed-at">
          {workspace.runUrl ? (
            <a href={workspace.runUrl}>{formatAgo(workspace.deployedAt, nowMs)}</a>
          ) : (
            <span>{formatAgo(workspace.deployedAt, nowMs)}</span>
          )}
        </div>
      )}
    </div>
  );
}

function WorkspaceDeploySummary({ repo, workspace, count, unobserved }: {
  repo: string; workspace: WorkspaceState; count: number; unobserved: string[];
}) {
  const { ref, sha, pr } = workspace;
  return (
    <div>
      {pr && (
        <div className="pr-line">
          <a href={pr.url} title={pr.title} className="truncate">
            #{pr.number} {pr.title}
          </a>
          {pr.merged && <span className="merged-badge">Merged</span>}
        </div>
      )}
      <div className="branch-sha-line">
        {ref && (
          <a href={branchUrl(repo, ref)} title={ref} className="truncate branch-link">
            {ref}
          </a>
        )}
        {sha && <a href={commitUrl(repo, sha)}>{sha.slice(0, 8)}</a>}
      </div>
      {count > 1 && <div className="workspace-count">{count} workspaces</div>}
      {/* Quieter than the "Workspaces differ" badge on purpose: this is a workspace that has never
          deployed, not two workspaces disagreeing about what is deployed. It is the steady state for
          a service that does not use all of its configured workspaces, so it must not read as an
          alarm - but it is still said out loud, because the count above otherwise implies the
          reading covers workspaces it does not. */}
      {unobserved.length > 0 && (
        <div className="unobserved-note">no reading yet: {unobserved.join(', ')}</div>
      )}
    </div>
  );
}

function MixedDeploySummary({ repo, workspaces }: { repo: string; workspaces: WorkspaceState[] }) {
  return (
    <div>
      <div className="mixed-badge">Workspaces differ</div>
      <ul className="workspace-diff-list">
        {workspaces.map((w) => (
          <li key={w.workspace}>
            {/* A workspace with no CURRENT row is named as unread rather than dashed out: "—" reads
                as "deployed nothing", and the difference between the two is the whole point of the
                row. */}
            {w.workspace}: {w.sha ? <a href={commitUrl(repo, w.sha)}>{w.sha.slice(0, 8)}</a> : <span className="no-reading">no reading</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeployedCell({ svc }: { svc: ServiceState }) {
  const { repo, workspaces, mixed } = svc;
  if (mixed) return <MixedDeploySummary repo={repo} workspaces={workspaces} />;
  const workspace = representativeWorkspace(workspaces);
  if (!workspace) return <span>—</span>;
  // The count is of workspaces that actually reported this reading; the rest are named below it
  // rather than folded into a number they do not belong to.
  const observed = workspaces.filter((w) => w.sha !== undefined);
  return (
    <WorkspaceDeploySummary
      repo={repo}
      workspace={workspace}
      count={observed.length}
      unobserved={workspaces.filter((w) => w.sha === undefined).map((w) => w.workspace)}
    />
  );
}

function WhoHasItCell({ holdState, reservation, workspace }: {
  holdState: HoldState; reservation?: Reservation; workspace?: WorkspaceState;
}) {
  const pill = holdState === 'restoring' && (
    <div className="pill pill-restoring">Restoring {workspace?.ref ?? 'main'}</div>
  );
  if (!reservation) return pill ? <div>{pill}</div> : <span>—</span>;
  return (
    <div>
      {pill}
      <div>{reservation.holder}</div>
      <div className="hold-reason">{reservation.reason}</div>
    </div>
  );
}

function ProgressBar({ pct, tone }: { pct: number; tone: 'brand' | 'warning' }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div
      className={`progress-bar progress-bar-${tone}`}
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="progress-bar-fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

/**
 * One inert control that names nobody, because "is this the viewer's own hold" is a question Vinz
 * cannot answer yet.
 *
 * `reservation.holder` is a GitHub handle (`alex-rewind`); the viewer is a Google identity
 * (`alex.smith@rewind.io`). Nothing maps one to the other, and the previous
 * `holder === viewerEmail.split('@')[0]` comparison is false for everyone whose handle is not their
 * email prefix — so it silently offered the holder a button reading "Nudge <their own name>". The
 * three actions need the answer in opposite directions (Extend and Release need "yes", Nudge needs
 * "no"), so with no answer none of them can be offered honestly.
 *
 * TODO(R5): R5 is what grants holds, so R5 is where the mapping lands — a viewer→handle lookup, or
 * a holder email carried on the reservation payload. Restore the Extend/Release/Nudge treatment and
 * the viewer identity this component needs to choose between them at the same time.
 */
function HeldActions() {
  return (
    <div className="actions">
      <button type="button" disabled title={R5_TITLE} className="btn-secondary">Hold actions arrive with R5</button>
    </div>
  );
}

function RetryAction() {
  return (
    <div className="actions">
      <button type="button" disabled title={R5_TITLE} className="btn-secondary">Retry restore</button>
    </div>
  );
}

function ForHowLongCell({ svc, nowMs }: { svc: ServiceState; nowMs: number }) {
  const { holdState, reservation, workspaces } = svc;
  switch (holdState) {
    case 'held': {
      if (!reservation) return <span>—</span>;
      const grantedMs = Date.parse(reservation.granted_at);
      const expiresMs = Date.parse(reservation.expires_at);
      const totalMs = expiresMs - grantedMs;
      const pct = totalMs > 0 ? ((nowMs - grantedMs) / totalMs) * 100 : 100;
      return (
        <div>
          <div className="duration-remaining">{formatLeft(reservation.expires_at, nowMs)}</div>
          <div className="duration-meta">until {formatLocal(reservation.expires_at)} · extended {reservation.extensions}×</div>
          <ProgressBar pct={pct} tone="brand" />
          <HeldActions />
        </div>
      );
    }
    case 'restoring': {
      const ref = representativeWorkspace(workspaces)?.ref ?? 'main';
      return (
        <div>
          {reservation && <div className="duration-remaining">Expired {formatAgo(reservation.expires_at, nowMs)}</div>}
          <div className="duration-meta">{ref} redeploying automatically</div>
          <ProgressBar pct={100} tone="warning" />
        </div>
      );
    }
    case 'restore_failed':
      return (
        <div>
          {reservation && <div className="duration-remaining">Expired {formatAgo(reservation.expires_at, nowMs)}</div>}
          <RetryAction />
        </div>
      );
    case 'free':
    default:
      return <span>—</span>;
  }
}

function CheckedCell({ check, nowMs }: { check: CheckStatus; nowMs: number }) {
  const age = check.lastSuccessAt && <div className="checked-age">{formatAgo(check.lastSuccessAt, nowMs)}</div>;
  if (check.state === 'current') {
    return (
      <div>
        <div>Current</div>
        {age}
      </div>
    );
  }
  if (check.state === 'cannot_check') {
    return (
      <div>
        <div>Can’t check</div>
        {check.reason && <div className="checked-reason">{check.reason}</div>}
        {age}
      </div>
    );
  }
  return (
    <div>
      <div>Never checked</div>
      {age}
    </div>
  );
}

export interface StatusTableProps {
  services: ServiceState[];
  nowMs: number;
}

export function StatusTable({ services, nowMs }: StatusTableProps) {
  return (
    <table>
      <thead>
        <tr>
          <th scope="col">Service</th>
          <th scope="col">Who has it</th>
          <th scope="col">For how long</th>
          <th scope="col">What is deployed</th>
          <th scope="col">Deployed by</th>
          <th scope="col">Checked</th>
        </tr>
      </thead>
      <tbody>
        {services.map((svc) => (
          <tr key={svc.repo} className={ROW_CLASS[svc.holdState] ?? ''}>
            <td>{svc.repo}</td>
            <td><WhoHasItCell holdState={svc.holdState} reservation={svc.reservation} workspace={representativeWorkspace(svc.workspaces)} /></td>
            <td><ForHowLongCell svc={svc} nowMs={nowMs} /></td>
            <td><DeployedCell svc={svc} /></td>
            <td><DeployedByCell workspace={representativeWorkspace(svc.workspaces)} nowMs={nowMs} /></td>
            <td><CheckedCell check={svc.check} nowMs={nowMs} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Status() {
  const { idToken } = useAuth();
  const { data, isPending, isError, error } = useStateQuery(idToken);

  if (isPending) return <div className="card">Loading services…</div>;
  if (isError) {
    return <div className="card">Couldn’t load service state: {error instanceof Error ? error.message : String(error)}</div>;
  }

  return (
    <div>
      <p className="timezone-note">Times shown in {viewerTimeZone()}.</p>
      <div className="card">
        <StatusTable services={data.services} nowMs={Date.now()} />
      </div>
    </div>
  );
}
